import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { BucketIcon, CalendarIcon, ChevronUpIcon, PencilIcon } from 'assets/icons';
import { IconWrapper, MainTitle } from 'common/components';
import { CustomTooltip } from 'common/components/CustomTooltip/CustomTooltip.styled';
import { getImageByExtension } from 'common/helpers';
import { useScreenSizeDetection } from 'common/hooks';
import { Button } from 'components/common';
import { Flex } from 'components/styled/Flex';

import {
  Card,
  CardBody,
  CardContentWrapper,
  CardDate,
  CardDateContainer,
  CardFooter,
  CardImage,
  CardTitle,
} from './DocumentItem.styled';

interface IProps {
  title?: string;
  titleMargin?: string;
  date?: string;
  border?: string;
  padding?: string;
  bottomTitle?: string;
  documentExtension?: string;
  bottomTitleText?: string;
  cardBodyPadding?: string;
  imageHeight?: string;
  onClick?: () => void;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
}

export const DocumentItem = ({
  title,
  titleMargin,
  date,
  border,
  padding,
  bottomTitle,
  documentExtension,
  bottomTitleText,
  cardBodyPadding,
  imageHeight,
  onClick,
  onDeleteClick,
  onEditClick,
}: IProps) => {
  const { t } = useTranslation();

  const { colors, fontFamily } = useTheme();

  const { isMobile } = useScreenSizeDetection();

  return (
    <>
      <CustomTooltip place="top" effect="solid" id="infoTooltip" multiline />

      <Card border={border}>
        <CardContentWrapper padding={padding} onClick={onClick}>
          {onDeleteClick && (
            <Flex justifyContent="flex-end">
              <IconWrapper
                data-testid="delete-document"
                $stroke={colors.black_80}
                onClick={onDeleteClick}
                data-for="infoTooltip"
                data-tip={t('Delete')}
              >
                <BucketIcon />
              </IconWrapper>
            </Flex>
          )}

          <CardBody padding={cardBodyPadding}>
            {title && <CardTitle margin={titleMargin}>{title}</CardTitle>}

            <CardImage
              height={imageHeight || '3.5rem'}
              src={getImageByExtension(documentExtension)}
              alt={title}
            />
          </CardBody>

          {date && (
            <CardDateContainer>
              <CalendarIcon />
              <CardDate>{date}</CardDate>
            </CardDateContainer>
          )}

          <div style={{ width: '100%', margin: !isMobile ? 0 : '0 0 0 1rem' }}>
            {bottomTitle && (
              <MainTitle
                fontFamily={fontFamily.semiBold}
                fontSize="1rem"
                fontWeight={600}
                color={colors.black_80}
                margin={!isMobile ? (bottomTitleText ? '0' : '0 0 0.5rem 0') : '0'}
              >
                {bottomTitle}
              </MainTitle>
            )}

            {bottomTitleText && (
              <MainTitle
                fontFamily={fontFamily.regular}
                fontSize="0.75rem"
                fontWeight={400}
                color={colors.black_70}
                margin={!isMobile ? '0.5rem 0' : '0'}
              >
                {bottomTitleText}
              </MainTitle>
            )}
          </div>

          <CardFooter>
            {onClick && !isMobile ? (
              <Button width="100%" color="white" onClick={onClick} label="Preview" />
            ) : (
              <IconWrapper margin="0.25rem 0 0 0" onClick={onClick}>
                <ChevronUpIcon rotate={90} />
              </IconWrapper>
            )}

            {onEditClick && (
              <Button
                leftIcon={
                  <Flex margin="0 0.5rem 0 0" data-testid={`edit-document`}>
                    <PencilIcon stroke={colors.black_80} />
                  </Flex>
                }
                width="100%"
                color="white"
                onClick={onEditClick}
                label="Edit"
                margin="0 0 0 0.5rem"
              />
            )}
          </CardFooter>
        </CardContentWrapper>
      </Card>
    </>
  );
};
